import { format } from 'date-fns';
import { parseSessionInstant } from './sessionInstant';
import { isArtifactTranscriptSourceType } from './sourceTypes';

type TranscriptSessionLike = { session_start_at?: string | null; transcript_id?: string };
type ArtifactSourceLike = { type?: string | null; value?: string | null };

// Dates use date-fns `format` so every value is the viewer's local calendar day.
const FULL_DATE_FORMAT = 'MMMM d, yyyy';
const FULL_MONTH_FORMAT = 'MMMM yyyy';
const SHORT_MONTH_FORMAT = 'MMM';
const SHORT_MONTH_YEAR_FORMAT = 'MMM yyyy';
const COMPACT_DATE_FORMAT = 'yyyy.MM.dd';

function parseSortedSessionInstants(values: Array<string | null | undefined>): Date[] | null {
  const dates = values.map((value) => parseSessionInstant(value));
  if (dates.some((date) => date === null)) {
    return null;
  }
  return (dates as Date[]).sort((left, right) => left.getTime() - right.getTime());
}

export function formatArtifactHeaderDateFromTranscriptSessions(
  transcriptSessions: TranscriptSessionLike[] | null | undefined,
  expectedSessionCount?: number | null
): string | null {
  if (!Array.isArray(transcriptSessions) || transcriptSessions.length === 0) {
    return null;
  }
  if (
    typeof expectedSessionCount === 'number' &&
    expectedSessionCount > 0 &&
    transcriptSessions.length !== expectedSessionCount
  ) {
    return null;
  }

  const sortedDates = parseSortedSessionInstants(
    transcriptSessions.map((session) => session?.session_start_at)
  );
  if (!sortedDates) {
    return null;
  }
  const first = sortedDates[0];
  const last = sortedDates[sortedDates.length - 1];

  if (sortedDates.length === 1) {
    return format(first, FULL_DATE_FORMAT);
  }

  const sameYear = first.getFullYear() === last.getFullYear();
  const sameMonth = sameYear && first.getMonth() === last.getMonth();
  if (sameMonth) {
    return format(first, FULL_MONTH_FORMAT);
  }

  if (sameYear) {
    return `${format(first, SHORT_MONTH_FORMAT)} - ${format(last, SHORT_MONTH_YEAR_FORMAT)}`;
  }

  return `${format(first, SHORT_MONTH_YEAR_FORMAT)} - ${format(last, SHORT_MONTH_YEAR_FORMAT)}`;
}

export function formatSingleArtifactHeaderDateFromTranscriptSessions(
  transcriptSessions: TranscriptSessionLike[] | null | undefined
): string | null {
  const firstSession = transcriptSessions?.[0];
  if (!firstSession) {
    return null;
  }
  const date = parseSessionInstant(firstSession.session_start_at);
  return date ? format(date, FULL_DATE_FORMAT) : null;
}

export function formatCompactArtifactBadgeDate(
  sessionStartsAt: Array<string | null | undefined> | null | undefined,
  expectedSessionCount?: number | null
): string | null {
  if (!Array.isArray(sessionStartsAt) || sessionStartsAt.length === 0) {
    return null;
  }
  if (
    typeof expectedSessionCount === 'number' &&
    expectedSessionCount > 0 &&
    sessionStartsAt.length !== expectedSessionCount
  ) {
    return null;
  }

  const sortedDates = parseSortedSessionInstants(sessionStartsAt);
  if (!sortedDates) {
    return null;
  }
  const firstDate = format(sortedDates[0], COMPACT_DATE_FORMAT);
  const lastDate = format(sortedDates[sortedDates.length - 1], COMPACT_DATE_FORMAT);
  return firstDate === lastDate ? firstDate : `${firstDate}/${lastDate}`;
}

export function countTranscriptSessionSources(
  sources: ArtifactSourceLike[] | null | undefined
): number | null {
  if (!Array.isArray(sources) || sources.length === 0) {
    return null;
  }

  const transcriptSourceIDs = new Set<string>();
  for (const source of sources) {
    const sourceType = source?.type?.trim().toLowerCase();
    const sourceValue = source?.value?.trim();
    if (!sourceValue) {
      continue;
    }
    if (isArtifactTranscriptSourceType(sourceType)) {
      transcriptSourceIDs.add(sourceValue);
    }
  }

  return transcriptSourceIDs.size > 0 ? transcriptSourceIDs.size : null;
}
